/**
 * Carrusel de vídeos UGC (sections/kit-ugc-videos.liquid).
 * - Flechas anterior/siguiente que desplazan el carril una tarjeta.
 * - Los vídeos se reproducen en silencio cuando están visibles y se pausan al salir.
 * - Botón por vídeo para activar el sonido (silencia el resto).
 */
class KitUgcVideos extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #track = null;

  connectedCallback() {
    this.#track = this.querySelector('.kit-ugc__track');
    if (!this.#track) return;

    this.addEventListener('click', this.#handleClick);
    this.#track.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.#updateArrows();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.dataset.autoplay === 'true' && !reducedMotion && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.6 });
      this.#videos.forEach((video) => this.#observer?.observe(video));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#track?.removeEventListener('scroll', this.#updateArrows);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    return [...this.querySelectorAll('video')];
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      if (entry.isIntersecting) {
        video.preload = 'auto';
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('.kit-ugc__arrow');
    if (arrow instanceof HTMLButtonElement) {
      this.#scrollBy(Number(arrow.dataset.direction) || 1);
      return;
    }

    const sound = event.target.closest('.kit-ugc__sound');
    if (sound instanceof HTMLButtonElement) {
      this.#toggleSound(sound);
      return;
    }

    const video = event.target.closest('video');
    if (video) {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }
  };

  /** @param {HTMLButtonElement} button */
  #toggleSound(button) {
    const video = button.closest('.kit-ugc__media')?.querySelector('video');
    if (!video) return;

    const enable = video.muted;
    for (const other of this.#videos) {
      other.muted = true;
      other.closest('.kit-ugc__media')?.querySelector('.kit-ugc__sound')?.setAttribute('aria-pressed', 'false');
    }

    video.muted = !enable;
    button.setAttribute('aria-pressed', String(enable));
    if (enable) video.play().catch(() => {});
  }

  /** @param {number} direction */
  #scrollBy(direction) {
    const track = this.#track;
    const slide = track?.firstElementChild;
    if (!track || !(slide instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (slide.offsetWidth + gap), behavior: 'smooth' });
  }

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;

    const prev = this.querySelector('.kit-ugc__arrow--prev');
    const next = this.querySelector('.kit-ugc__arrow--next');
    const maxScroll = track.scrollWidth - track.clientWidth - 2;

    if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= maxScroll;
  };
}

if (!customElements.get('kit-ugc-videos')) {
  customElements.define('kit-ugc-videos', KitUgcVideos);
}
